// A user's bio: rich text written in the same RichTextEditor as a product's
// long description (its `blockFormatting` mode: marks, headings, lists,
// alignment and spacing, no media), stored as sanitized HTML in `users/{uid}.bio`
// and shown on the public profile, the dashboard profile and the
// server-rendered copy of the profile.
//
// PURE ON PURPOSE. Nothing here touches the DOM or DOMPurify, so it loads in the
// node test runner and in a server component that only needs text (the meta
// description and the JSON-LD). Sanitizing is `sanitizeBioHtml` in
// src/lib/sanitize-html.ts, and rendering is ProfileBio in
// src/components/profile/profile-bio.tsx; the two are kept out of this file so a
// text-only consumer does not pull a sanitizer (and on the server, jsdom) in.
//
// THE USER DOCUMENT IS WORLD-READABLE and written straight from the client, so
// the stored bio is untrusted whatever the dashboard did before saving it: a
// forged write can put anything under the length cap in it. Every render path
// goes through bioHtml() and then sanitizeBioHtml(), and ProfileBio is the one
// place that does both.

/**
 * The bio's cap on VISIBLE characters: the editor's `limit`, and what the
 * counter under it shows. Markup does not count against it. UI-only, like
 * NOTE_MAX_LENGTH (src/lib/profile-notes.ts): firestore.rules cannot count
 * visible text, so the binding bound is BIO_HTML_MAX_LENGTH below.
 *
 * The same number the plain-text bio had, so nobody's existing bio is over it.
 */
export const BIO_MAX_CHARS = 1000;

/**
 * The cap on the RAW stored HTML, markup included. MIRRORS `bioValid()` in
 * firestore.rules, which is the only server-side bound on the field (a test
 * reads the rule and checks the number). Five times the visible limit, so a
 * fully formatted 1000-character bio fits with room to spare while a forged
 * write stays bounded on a document every profile view downloads.
 *
 * The dashboard refuses to save above it (checkBioForSave) rather than letting
 * the rules reject the write, so the owner is told what to change instead of
 * seeing a permission error.
 */
export const BIO_HTML_MAX_LENGTH = 5000;

/** The block tags the editor's output always opens with. See isBioHtml. */
const EDITOR_HTML_RE = /^<(?:p|h[1-6]|ul|ol|blockquote|pre)(?:\s[^>]*)?>/i;

/**
 * Does a stored bio come from the editor, as opposed to being a plain-text bio
 * written before the editor existed?
 *
 * STRICTER THAN noteHtml's test, deliberately. noteHtml treats a note as markup
 * when ANY tag-shaped run appears in it. That is safe (the result is sanitized
 * either way) but lossy for text: a plain-text bio reading "email me <at> my
 * name" would be taken as markup and the sanitizer would drop "<at>". The
 * editor's output always OPENS with one of the block tags below (an empty
 * editor is `<p></p>`), and a plain-text bio that opens with one is vanishingly
 * unlikely, so testing the start keeps every legacy bio's words intact.
 *
 * Only fidelity rides on this, never safety: both branches end in
 * sanitizeBioHtml.
 */
export function isBioHtml(stored: string): boolean {
    return EDITOR_HTML_RE.test(stored.trimStart());
}

function escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * The HTML to render (or to seed the editor with) for a stored bio.
 *
 * Editor-authored bios pass through untouched. Legacy plain-text bios are
 * escaped and given the paragraph and line-break structure their whitespace
 * implied, so they read the way they did under `whitespace-pre-wrap`. That
 * conversion is noteHtml's (src/lib/profile-notes.ts), kept identical on
 * purpose and checked against it by a test; only the markup DETECTION differs
 * (see EDITOR_HTML_RE).
 *
 * Seeding the editor with this rather than the raw string matters for the
 * legacy case: Tiptap parses its `content` as HTML, so a plain-text bio handed
 * over raw would lose every line break and become one paragraph.
 *
 * The result is still untrusted: pipe it through `sanitizeBioHtml()`, or render
 * it with ProfileBio, which does.
 *
 * Sliced to BIO_HTML_MAX_LENGTH BEFORE converting, so a legacy bio full of
 * `&` (which grows as it is escaped) is never cut mid-sentence by a later cap.
 * Slicing HTML can cut a tag in half; the sanitizer drops the partial tag.
 */
export function bioHtml(stored: unknown): string {
    if (typeof stored !== 'string' || !stored) return '';
    const text = stored.slice(0, BIO_HTML_MAX_LENGTH);
    if (isBioHtml(text)) return text;
    return text
        .replace(/\r\n?/g, '\n')
        .split(/\n{2,}/)
        .map(block => block.trim())
        .filter(Boolean)
        .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
        .join('');
}

/**
 * Tags that end a line of text. Replaced with a space rather than removed, so
 * the last word of one paragraph does not run into the first word of the next
 * ("shop.<p>New" must read "shop. New", not "shop.New"). Every other tag is an
 * inline mark and is removed outright, so "su<strong>pa</strong>" stays one
 * word.
 */
const BLOCK_BOUNDARY_RE = /<\/?(?:p|h[1-6]|ul|ol|li|blockquote|pre|br|div)\b[^>]*>/gi;

// A Map rather than an object literal: `&constructor;` in a forged bio would
// otherwise look up Object.prototype.constructor and decode to the source of a
// function.
const NAMED_ENTITIES = new Map<string, string>([
    ['amp', '&'], ['lt', '<'], ['gt', '>'], ['quot', '"'], ['apos', "'"], ['nbsp', ' '],
]);

/**
 * Decode the entities a serializer actually writes into text: the five named
 * ones, `&nbsp;`, and numeric references. `&amp;` is decoded in the SAME pass
 * as the rest, never before them, so `&amp;lt;` reads "&lt;" (what the author
 * typed) and not "<".
 */
function decodeEntities(text: string): string {
    return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (whole, body: string) => {
        if (body[0] === '#') {
            const code = body[1] === 'x' || body[1] === 'X'
                ? parseInt(body.slice(2), 16)
                : parseInt(body.slice(1), 10);
            // Out-of-range and surrogate code points would throw in
            // fromCodePoint; they are left as written rather than guessed at.
            if (!Number.isFinite(code) || code < 1 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) {
                return whole;
            }
            return String.fromCodePoint(code);
        }
        return NAMED_ENTITIES.get(body.toLowerCase()) ?? whole;
    });
}

/**
 * The bio as one line of plain text. Works without a DOM.
 *
 * NOT FOR A STORED BIO ON ITS OWN. It keeps the text of every element, so on a
 * forged bio it would also return text the sanitizer removes (a `<template>`'s,
 * a `<script>`'s). The meta description, the JSON-LD and any other consumer of
 * a stored bio use bioDisplayText (src/lib/sanitize-html.ts), which sanitizes
 * and then calls this.
 *
 * Tags are stripped (block boundaries become spaces, inline marks vanish),
 * entities are decoded, and whitespace is collapsed. The result is TEXT: every
 * consumer must escape it as text (React and the JSON-LD component both do),
 * never interpolate it into HTML, because decoding turns "&lt;" back into "<".
 *
 * A legacy plain-text bio is only whitespace-collapsed: its "&amp;" was typed by
 * the author and is not an entity.
 */
export function bioPlainText(stored: unknown): string {
    if (typeof stored !== 'string' || !stored) return '';
    const text = stored.slice(0, BIO_HTML_MAX_LENGTH);
    const plain = isBioHtml(text)
        ? decodeEntities(text.replace(BLOCK_BOUNDARY_RE, ' ').replace(/<[^>]*>/g, ''))
        : text;
    return plain.replace(/\s+/g, ' ').trim();
}

/**
 * True when a bio (stored, or fresh from the editor) carries no visible text.
 * An empty editor still reports `<p></p>`, so this is what decides that the
 * dashboard saves an empty string, and what a render site checks before
 * drawing the bio's section at all.
 *
 * Judged on whatever string it is given. Handed the raw STORED bio, a forged
 * one whose only text sits inside a tag the sanitizer removes with its
 * content (a `<script>`, say) counts as not blank here and still renders as
 * nothing, so a render site deciding whether to draw the bio's section uses
 * bioDisplayText (src/lib/sanitize-html.ts), which sanitizes first. ProfileBio
 * and the dashboard's save both call this on sanitized HTML already.
 */
export function isBioBlank(stored: unknown): boolean {
    return bioPlainText(stored).length === 0;
}

export type BioSaveCheck =
    | { ok: true; value: string }
    | { ok: false; message: string };

/**
 * What the dashboard should write for an edited bio, or why it cannot.
 *
 * Pass the SANITIZED editor output (`sanitizeBioHtml(html)`), so the check
 * measures exactly the bytes that will be stored and bioValid() in
 * firestore.rules will judge. A blank bio saves as the empty string, which is
 * what every "no bio" check in the app already understands; anything over
 * BIO_HTML_MAX_LENGTH is refused with a message the owner can act on, instead
 * of being written and bounced by the rules as a permission error.
 *
 * The visible-character limit is not re-checked here: the editor's `limit`
 * already stops typing and trims a paste at BIO_MAX_CHARS, and it counts
 * characters in a way this file cannot reproduce exactly from HTML. The raw
 * cap is the one the server enforces, so it is the one that has to be right.
 */
export function checkBioForSave(sanitizedHtml: string): BioSaveCheck {
    const html = typeof sanitizedHtml === 'string' ? sanitizedHtml : '';
    if (isBioBlank(html)) return { ok: true, value: '' };
    if (html.length > BIO_HTML_MAX_LENGTH) {
        return {
            ok: false,
            message: 'Your bio is too long to save with this much formatting. Shorten it or remove some formatting, then try again.',
        };
    }
    return { ok: true, value: html };
}
